/**
 * Discounts Hook using TanStack Query
 * Simplified discount code management with automatic cache invalidation
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import { DiscountsResource } from '../../core/resources/discounts';
import { getGlobalApiClient } from './useApiQuery';
export function useDiscountsQuery(options = {}) {
    const { sessionId, enabled = true, onApplySuccess, onRemoveSuccess } = options;
    const queryClient = useQueryClient();
    // Create discounts resource client
    const discountsResource = useMemo(() => {
        try {
            return new DiscountsResource(getGlobalApiClient());
        }
        catch (error) {
            throw new Error('Failed to initialize discounts resource: ' +
                (error instanceof Error ? error.message : 'Unknown error'));
        }
    }, []);
    // Main discounts query
    const { data: appliedDiscounts, isLoading: isFetching, error: fetchError, refetch: refetchDiscounts, } = useQuery({
        queryKey: ['discounts', sessionId],
        queryFn: () => discountsResource.getAppliedDiscounts(sessionId),
        enabled: enabled && !!sessionId,
        staleTime: 30000, // 30 seconds
        refetchOnWindowFocus: false,
    });
    // Apply discount code mutation
    const applyMutation = useMutation({
        mutationFn: ({ code }) => {
            if (!sessionId) {
                throw new Error('No checkout session available');
            }
            if (!code || code.trim() === '') {
                throw new Error('Discount code is required');
            }
            return discountsResource.applyDiscountCode(sessionId, code);
        },
        onSuccess: async (response) => {
            if (response.success) {
                // Invalidate both discounts and checkout queries
                if (sessionId) {
                    await Promise.all([
                        queryClient.invalidateQueries({ queryKey: ['discounts', sessionId] }),
                        queryClient.invalidateQueries({ queryKey: ['checkout'] }),
                        queryClient.invalidateQueries({ queryKey: ['shipping-rates', sessionId] }),
                        queryClient.invalidateQueries({ queryKey: ['shipping-rates-preview', sessionId] }),
                    ]);
                }
                // Call onSuccess callback if provided
                if (onApplySuccess) {
                    onApplySuccess(response.promotion);
                }
            }
        },
    });
    // Remove discount mutation
    const removeMutation = useMutation({
        mutationFn: ({ discountId }) => {
            if (!sessionId) {
                throw new Error('No checkout session available');
            }
            if (!discountId) {
                throw new Error('Discount ID is required');
            }
            return discountsResource.removeDiscount(sessionId, discountId);
        },
        onSuccess: async (response) => {
            if (response.success) {
                // Invalidate both discounts and checkout queries
                if (sessionId) {
                    await Promise.all([
                        queryClient.invalidateQueries({ queryKey: ['discounts', sessionId] }),
                        queryClient.invalidateQueries({ queryKey: ['checkout'] }),
                        queryClient.invalidateQueries({ queryKey: ['shipping-rates', sessionId] }),
                        queryClient.invalidateQueries({ queryKey: ['shipping-rates-preview', sessionId] }),
                    ]);
                }
                // Call onSuccess callback if provided
                if (onRemoveSuccess) {
                    onRemoveSuccess();
                }
            }
        },
    });
    // Apply discount code function
    const applyDiscountCode = useCallback(async (code) => {
        try {
            const response = await applyMutation.mutateAsync({ code });
            if (response.success) {
                return { success: true, discount: response.promotion };
            }
            else {
                return {
                    success: false,
                    error: response.error?.message || 'Failed to apply discount code',
                };
            }
        }
        catch (err) {
            // Extract error message from API response
            let errorMessage = 'Failed to apply discount code';
            if (err && typeof err === 'object') {
                const error = err;
                if (error.response?.data?.error?.message) {
                    errorMessage = error.response.data.error.message;
                }
                else if (error.response?.data?.message) {
                    errorMessage = error.response.data.message;
                }
                else if (error.message) {
                    errorMessage = error.message;
                }
            }
            return { success: false, error: errorMessage };
        }
    }, [applyMutation]);
    // Remove discount function
    const removeDiscount = useCallback(async (discountId) => {
        try {
            const response = await removeMutation.mutateAsync({ discountId });
            if (response.success) {
                return { success: true };
            }
            else {
                return {
                    success: false,
                    error: response.error?.message || 'Failed to remove discount',
                };
            }
        }
        catch (err) {
            // Extract error message from API response
            let errorMessage = 'Failed to remove discount';
            if (err && typeof err === 'object') {
                const error = err;
                if (error.response?.data?.error?.message) {
                    errorMessage = error.response.data.error.message;
                }
                else if (error.response?.data?.message) {
                    errorMessage = error.response.data.message;
                }
                else if (error.message) {
                    errorMessage = error.message;
                }
            }
            return { success: false, error: errorMessage };
        }
    }, [removeMutation]);
    // Refetch function
    const refresh = useCallback(async () => {
        await refetchDiscounts();
    }, [refetchDiscounts]);
    // Clear error function
    const clearError = useCallback(() => {
        // TanStack Query doesn't provide a direct way to clear errors
        // We can trigger a refetch which will clear the error
        void refresh();
    }, [refresh]);
    // Combine loading states
    const isLoading = isFetching;
    const isApplying = applyMutation.isPending;
    const isRemoving = removeMutation.isPending;
    // Combine errors
    const error = (fetchError || applyMutation.error || removeMutation.error);
    return {
        appliedDiscounts,
        isLoading,
        isApplying,
        isRemoving,
        error,
        applyDiscountCode,
        removeDiscount,
        refresh,
        clearError,
    };
}
